import { type } from 'arktype';

export interface AddressResponse {
  id: string;
  street: string;
  zip: string;
  city: string;
  country: string;
  data: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export const addressQuerySchema = type({
  'street?': type('string').configure({
    description: 'Filter addresses by street name (partial match). Optional.',
    examples: ['Bahnhofstrasse'],
  }),
  'zip?': type('string').configure({
    description: 'Filter addresses by postal/ZIP code. Optional.',
    examples: ['8001'],
  }),
  'city?': type('string').configure({
    description: 'Filter addresses by city (partial match). Optional.',
    examples: ['Zürich'],
  }),
  'country?': type('string').configure({
    description: 'Filter addresses by ISO 3166-1 alpha-2 country code. Optional.',
    examples: ['CH'],
  }),
}).configure({ description: 'Query parameters for filtering the address list endpoint.' });

export type AddressQuery = typeof addressQuerySchema.t;

export const createAddressSchema = type({
  street: type('string').configure({
    description: 'Street name and house number.',
    examples: ['Bahnhofstrasse 1'],
  }),
  zip: type('string').configure({
    description: 'Postal/ZIP code.',
    examples: ['8001'],
  }),
  city: type('string').configure({
    description: 'City name.',
    examples: ['Zürich'],
  }),
  country: type('string').configure({
    description: 'ISO 3166-1 alpha-2 country code (uppercase).',
    examples: ['CH'],
  }),
  'data?': type('object | null').configure({
    description: 'Free-form structured metadata for the address (e.g. geocoding data). Optional and nullable.',
    examples: [{ lat: 47.3769, lng: 8.5417 }],
  }),
}).configure({ description: 'Payload to create a new postal address record.' });

export type CreateAddress = typeof createAddressSchema.t;

export const updateAddressSchema = type({
  'street?': type('string').configure({
    description: 'Updated street name and house number. Optional.',
    examples: ['Bahnhofstrasse 2'],
  }),
  'zip?': type('string').configure({
    description: 'Updated postal/ZIP code. Optional.',
    examples: ['8002'],
  }),
  'city?': type('string').configure({
    description: 'Updated city name. Optional.',
    examples: ['Zürich'],
  }),
  'country?': type('string').configure({
    description: 'Updated ISO 3166-1 alpha-2 country code. Optional.',
    examples: ['CH'],
  }),
  'data?': type('object | null').configure({
    description: 'Updated free-form structured metadata. Optional and nullable (pass null to clear).',
    examples: [{ lat: 47.3769, lng: 8.5417 }],
  }),
}).configure({ description: 'Partial update payload for an existing postal address record.' });

export type UpdateAddress = typeof updateAddressSchema.t;
